<template>
  <div class="avatar" :class="`avatar--${size}`" :style="placeholderStyle">
    <img v-if="resolvedSrc" :src="resolvedSrc" :alt="alt ?? name ?? ''" loading="lazy" decoding="async" />
    <div v-else class="avatar-fallback" aria-hidden="true">{{ initial }}</div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue"
import { tryImageObjectUrl } from "../../services/imageService"
import { safeCssColor } from "../../utils/cssColor"

/**
 * User/member avatar: photo when there is one, first initial of `name`
 * (display name or email) on the ink disc when there isn't. Sizes: sm (34px,
 * sidebar rows), md (44px, member lists), xl (80px, profile headers).
 *
 * Two photo sources: `src` for an external URL (the Google/Apple photo that
 * came with the sign-in) and `imageId` for one the user uploaded, which is
 * fetched through the scope-checked images API. An uploaded photo wins — it's
 * the more deliberate choice of the two.
 */
const props = withDefaults(
  defineProps<{
    src?: string | null
    /** Uploaded avatar (images row id); takes precedence over `src`. */
    imageId?: string | null
    /** Mean pixel of the uploaded photo, shown until it loads. */
    avgColor?: string | null
    /** Display name or email — the fallback initial comes from it. */
    name?: string | null
    /** Explicit img alt; defaults to `name`. */
    alt?: string
    size?: "sm" | "md" | "xl"
  }>(),
  { size: "md" },
)

const initial = computed(() => props.name?.trim().charAt(0).toUpperCase() || "?")

const uploaded = ref<string | null>(null)
watch(
  () => props.imageId,
  async (imageId) => {
    uploaded.value = null
    if (!imageId) return
    const url = await tryImageObjectUrl(imageId)
    if (props.imageId === imageId) uploaded.value = url
  },
  { immediate: true },
)

const resolvedSrc = computed(() => uploaded.value ?? props.src ?? null)

// While an uploaded photo is loading, the disc is its average colour rather
// than the ink fallback — no flash of a stranger's initial over their face.
const placeholderStyle = computed(() => {
  if (!props.imageId || uploaded.value) return undefined
  const color = safeCssColor(props.avgColor)
  return color ? { background: color } : undefined
})
</script>
